'use client';

import {
  CodeBlock,
  CodeBlockCopyButton,
} from '@cotera/client/v0/components/ai-elements/code-block';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { cn } from '@cotera/client/v0/lib/utils';
import { ChevronDown, CopyIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import {
  useAnalytics,
  useCocoConversationId,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';

const JSON_STRINGIFY_SPACING = 2;
const HIGHLIGHT_THRESHOLD = 8_000;
const DEFAULT_EXPANDED_DEPTH = 0;
const ARRAY_PREVIEW_COUNT = 20;
const STRING_PREVIEW_LENGTH = 200;

const isErrorOutput = (value: unknown): value is { error: string } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 1 &&
    'error' in value &&
    typeof (value as { error: unknown }).error === 'string'
  );
};

export const ToolOutputViewer = ({ value }: { value: unknown }) => {
  const stringified = useMemo(() => {
    try {
      return JSON.stringify(value, null, JSON_STRINGIFY_SPACING);
    } catch {
      return null;
    }
  }, [value]);

  if (isErrorOutput(value)) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
        <div className="mb-2 border-b border-amber-200 pb-1 text-xs font-medium uppercase tracking-wide opacity-80 dark:border-amber-900/40">
          Tool error
        </div>
        <div className="whitespace-pre-wrap break-words">{value.error}</div>
      </div>
    );
  }

  if (stringified === null) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        Unable to display tool output. See console for details.
      </div>
    );
  }

  if (stringified.length <= HIGHLIGHT_THRESHOLD) {
    return (
      <CodeBlock code={stringified} language="json" wrapLines>
        <ToolOutputCopyButton />
      </CodeBlock>
    );
  }

  return (
    <LargeJsonViewer
      value={value}
      stringified={stringified}
      rootLabel="output"
    />
  );
};

type LargeJsonViewerProps = {
  value: unknown;
  stringified: string;
  rootLabel: string;
};

const LargeJsonViewer = ({
  value,
  stringified,
  rootLabel,
}: LargeJsonViewerProps) => {
  return (
    <div className="group relative max-h-96 overflow-auto rounded-md border bg-muted/30">
      <div className="sticky top-0 z-10 flex justify-end bg-gradient-to-b from-background to-transparent p-2">
        <CopyRawButton code={stringified} />
      </div>
      <div className="space-y-1 p-4 font-mono text-xs text-foreground">
        <JsonNode label={rootLabel} value={value} depth={0} defaultExpanded />
      </div>
    </div>
  );
};

const ToolOutputCopyButton = () => {
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';

  return (
    <CodeBlockCopyButton
      onCopy={() => {
        if (cocoConversationId !== null) {
          analytics.track(CocoEvent.CodeBlockCopied, {
            conversationId: cocoConversationId,
            surface,
            language: 'json',
            source: 'tool-output',
          });
        }
      }}
    />
  );
};

const CopyRawButton = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';

  const handleCopy = useCallback(async () => {
    if (
      typeof window === 'undefined' ||
      navigator?.clipboard?.writeText === undefined
    ) {
      // eslint-disable-next-line no-console
      console.warn('Clipboard API not available');
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      if (cocoConversationId !== null) {
        analytics.track(CocoEvent.CodeBlockCopied, {
          conversationId: cocoConversationId,
          surface,
          language: 'json',
          source: 'tool-output-large',
        });
      }
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy JSON output', error);
    }
  }, [code, analytics, cocoConversationId, surface]);

  return (
    <Button
      className="h-7 px-2 text-xs"
      onClick={handleCopy}
      size="sm"
      variant="ghost"
    >
      <CopyIcon className="mr-1 size-3" />
      {copied ? 'Copied' : 'Copy raw'}
    </Button>
  );
};

type JsonNodeProps = {
  label?: string;
  value: unknown;
  depth: number;
  defaultExpanded?: boolean;
};

const JsonNode = ({
  label,
  value,
  depth,
  defaultExpanded = depth <= DEFAULT_EXPANDED_DEPTH,
}: JsonNodeProps) => {
  if (value === null || typeof value !== 'object') {
    return <PrimitiveNode depth={depth} label={label} value={value} />;
  }

  if (Array.isArray(value)) {
    return (
      <ArrayNode
        depth={depth}
        label={label}
        value={value}
        defaultExpanded={defaultExpanded}
      />
    );
  }

  return (
    <ObjectNode
      depth={depth}
      label={label}
      value={value as Record<string, unknown>}
      defaultExpanded={defaultExpanded}
    />
  );
};

const PrimitiveNode = ({
  depth,
  label,
  value,
}: Pick<JsonNodeProps, 'depth' | 'label'> & { value: unknown }) => {
  if (typeof value === 'string') {
    return <StringValue depth={depth} label={label} value={value} />;
  }

  const displayValue = formatPrimitiveValue(value);
  return (
    <div
      className={cn(
        'whitespace-pre-wrap break-all text-foreground/90',
        depth > 0 && 'pl-4'
      )}
    >
      {label ? <span className="text-foreground/60">{label}: </span> : null}
      <span>{displayValue}</span>
    </div>
  );
};

const formatPrimitiveValue = (value: unknown): string => {
  switch (typeof value) {
    case 'number':
      return String(value);
    case 'boolean':
      return value ? 'true' : 'false';
    case 'undefined':
      return 'undefined';
    case 'bigint':
      return `${value}n`;
    case 'symbol':
      return value.toString();
    case 'function':
      return '[Function]';
    default:
      return 'null';
  }
};

const StringValue = ({
  depth,
  label,
  value,
}: Pick<JsonNodeProps, 'depth' | 'label'> & { value: string }) => {
  const [expanded, setExpanded] = useState(
    value.length <= STRING_PREVIEW_LENGTH
  );
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  const displayValue = expanded
    ? JSON.stringify(value)
    : `${JSON.stringify(value.slice(0, STRING_PREVIEW_LENGTH))}… (${
        value.length
      } chars)`;

  return (
    <div
      className={cn(
        'whitespace-pre-wrap break-all text-foreground/90',
        depth > 0 && 'pl-4'
      )}
    >
      {label ? <span className="text-foreground/60">{label}: </span> : null}
      <span>{displayValue}</span>
      {value.length > STRING_PREVIEW_LENGTH ? (
        <Button
          className="ml-2 h-6 px-2 text-[10px]"
          onClick={toggle}
          size="sm"
          variant="ghost"
        >
          {expanded ? 'Show less' : 'Show full'}
        </Button>
      ) : null}
    </div>
  );
};

const ObjectNode = ({
  depth,
  label,
  value,
  defaultExpanded,
}: {
  depth: number;
  label?: string;
  value: Record<string, unknown>;
  defaultExpanded: boolean;
}) => {
  const [open, setOpen] = useState(defaultExpanded);
  const entries = useMemo(() => Object.entries(value), [value]);
  const isEmpty = entries.length === 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn('flex items-start gap-1', depth > 0 && 'pl-4')}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="mt-0.5 flex size-5 items-center justify-center rounded-sm border border-transparent text-muted-foreground transition hover:border-border hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <ChevronDown
              className={cn(
                'size-4 transition-transform',
                open ? 'rotate-0' : '-rotate-90'
              )}
            />
          </button>
        </CollapsibleTrigger>
        <div className="flex-1">
          <div className="text-foreground/90">
            {label ? (
              <span className="text-foreground/60">{label}: </span>
            ) : null}
            {'{'}
            {isEmpty ? '}' : '…'}
          </div>
        </div>
      </div>
      <CollapsibleContent>
        <div className="space-y-1 pl-6">
          {isEmpty ? (
            <div className="text-muted-foreground/70">{'// empty object'}</div>
          ) : (
            entries.map(([key, child]) => (
              <JsonNode key={key} label={key} value={child} depth={depth + 1} />
            ))
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ArrayNode = ({
  depth,
  label,
  value,
  defaultExpanded,
}: {
  depth: number;
  label?: string;
  value: unknown[];
  defaultExpanded: boolean;
}) => {
  const [open, setOpen] = useState(defaultExpanded);
  const [visibleCount, setVisibleCount] = useState(
    Math.min(value.length, ARRAY_PREVIEW_COUNT)
  );

  const hasMore = value.length > visibleCount;
  const showAll = useCallback(
    () => setVisibleCount(value.length),
    [value.length]
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn('flex items-start gap-1', depth > 0 && 'pl-4')}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="mt-0.5 flex size-5 items-center justify-center rounded-sm border border-transparent text-muted-foreground transition hover:border-border hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <ChevronDown
              className={cn(
                'size-4 transition-transform',
                open ? 'rotate-0' : '-rotate-90'
              )}
            />
          </button>
        </CollapsibleTrigger>
        <div className="flex-1 text-foreground/90">
          {label ? <span className="text-foreground/60">{label}: </span> : null}
          {'['}
          {value.length} item{value.length === 1 ? '' : 's'}
          {value.length === 0 ? ']' : ']…'}
        </div>
      </div>
      <CollapsibleContent>
        <div className="space-y-1 pl-6">
          {value.length === 0 ? (
            <div className="text-muted-foreground/70">{'// empty array'}</div>
          ) : (
            value
              .slice(0, visibleCount)
              .map((item, index) => (
                <JsonNode
                  key={index}
                  label={`${index}`}
                  value={item}
                  depth={depth + 1}
                />
              ))
          )}
          {hasMore ? (
            <Button
              className="mt-1 h-7 px-2 text-xs"
              onClick={showAll}
              size="sm"
              variant="ghost"
            >
              Show remaining {value.length - visibleCount} item
              {value.length - visibleCount === 1 ? '' : 's'}
            </Button>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
